import {
  FilterFieldConfig,
  DEFAULT_DATE_PRESETS,
  inferFilterType,
  createFilterConfig,
  FilterOption,
} from "src/config/filterConfig";
import { DatasetField } from "../datasetViewer/DatasetViewer";

// ============================================================================
// Backend Filter Schema Types
// ============================================================================

/**
 * Field metadata from the backend /filter-schema endpoint
 */
export interface BackendFilterField {
  name: string;
  label: string;
  type: string; // "string", "int", "float", "bool", "datetime"
  filter_type: string; // "multiSelect", "text", "range", "boolean", "dateRange"
  distinct_count?: number;
  min_value?: number | string;
  max_value?: number | string;
}

/**
 * Response from the backend /filter-schema endpoint
 */
export interface BackendFilterSchema {
  fields: BackendFilterField[];
}

/**
 * Histogram bucket from backend
 */
export interface BackendHistogramBucket {
  label: string;
  min: number;
  max: number;
  count: number;
}

/**
 * Response from the backend /facets endpoint
 */
export interface BackendFacetsResponse {
  facets: Record<string, { value: string; label: string; count: number }[]>;
  numeric_ranges?: Record<string, { min_value: number; max_value: number }>;
  numeric_histograms?: Record<
    string,
    { min_value: number; max_value: number; buckets: BackendHistogramBucket[] }
  >;
  date_ranges?: Record<string, { min_date: string; max_date: string }>;
  total_count: number;
  filtered_count: number;
}

/**
 * Dataset-specific filter configurations
 *
 * These configs define how filters appear in the sidebar for different dataset types.
 */

// ============================================================================
// Document Dataset Filters
// ============================================================================

export const documentDatasetFilters: FilterFieldConfig[] = [
  {
    fieldName: "file_type",
    label: "File Type",
    type: "multiSelect",
    deriveFromData: true,
    defaultExpanded: true,
    order: 1,
    showCounts: true,
    maxVisibleOptions: 8,
  },
  {
    fieldName: "document_type",
    label: "Document Type",
    type: "multiSelect",
    deriveFromData: true,
    defaultExpanded: true,
    order: 2,
    showCounts: true,
  },
  {
    // Options + counts come from the backend /facets endpoint
    // (mre/crud/dataset_crud.py::get_facets_for_dataset → virtual_fields
    // registry). The backend's value_labels supply the human-readable
    // labels per ProcessingStatus value. See issue #18798.
    fieldName: "processing_status",
    label: "Processing Status",
    type: "multiSelect",
    defaultExpanded: false,
    order: 3,
    showCounts: true,
  },
  {
    fieldName: "author",
    label: "Author",
    type: "multiSelect",
    deriveFromData: true,
    defaultExpanded: false,
    order: 4,
    searchable: true,
    showCounts: true,
  },
  {
    fieldName: "create_date",
    label: "Date Added",
    type: "dateRange",
    datePresets: DEFAULT_DATE_PRESETS,
    defaultExpanded: false,
    order: 5,
  },
  {
    fieldName: "title",
    label: "Title",
    type: "text",
    placeholder: "Search by title...",
    defaultExpanded: false,
    order: 6,
  },
];

// ============================================================================
// Tabular Dataset Filters (Dynamic)
// ============================================================================

/**
 * Generates filter configurations from dataset fields
 *
 * Automatically infers the best filter type based on field name patterns:
 * - Fields with "date" → dateRange
 * - Fields with "status", "type", "category" → multiSelect
 * - Fields with "is_", "has_", "can_" → boolean
 * - Fields with "price", "amount", "count" → range
 * - Other fields → text
 */
export function generateFiltersFromFields(
  fields: DatasetField[],
  sampleData?: Record<string, unknown>[],
): FilterFieldConfig[] {
  // Fields to exclude from filtering
  const excludeFields = new Set([
    "id",
    "source_uid",
    "dataset_fk",
    "Searchable Sections",
    "searchable_sections",
    "extracted metadata",
    "Extracted Metadata",
    "extracted_metadata",
  ]);

  return fields
    .filter((field) => !excludeFields.has(field.field_name))
    .map((field, index) => {
      // Get sample values for this field if available
      const sampleValues = sampleData
        ?.map((row) => row[field.field_name])
        .filter((v) => v !== null && v !== undefined);

      const inferredType = inferFilterType(field.field_name, sampleValues);

      return createFilterConfig(field.field_name, {
        type: inferredType,
        defaultExpanded: index < 3,
        order: index,
        // For multiSelect, derive options from data
        deriveFromData: inferredType === "multiSelect",
        showCounts: true,
      });
    });
}

/**
 * Gets the appropriate filter configuration for a dataset
 */
export function getFilterConfigForDataset(
  datasetType: string | undefined,
  datasetFields: DatasetField[],
  sampleData?: Record<string, unknown>[],
): FilterFieldConfig[] {
  if (datasetType === "document") {
    return documentDatasetFilters;
  }

  // For tabular datasets, generate from fields
  return generateFiltersFromFields(datasetFields, sampleData);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Prettifies a value for display (e.g., "pdf" → "PDF", "in_progress" → "In Progress")
 */
function prettifyValue(value: string): string {
  // Common abbreviations that should be uppercase
  const upperCaseValues = new Set([
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "csv",
    "txt",
    "xml",
    "json",
    "html",
    "css",
    "js",
    "ts",
    "id",
    "url",
    "api",
  ]);

  const lower = value.toLowerCase();
  if (upperCaseValues.has(lower)) {
    return value.toUpperCase();
  }

  // Title case for snake_case or regular text
  return value
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

// ============================================================================
// Facet Data Utilities
// ============================================================================

/**
 * Derives facet options from current data
 *
 * This is a fallback when the backend facet endpoint isn't available.
 * Counts unique values in the data for multiSelect filters.
 */
export function deriveFacetsFromData(
  data: Record<string, unknown>[],
  filterConfig: FilterFieldConfig[],
): Record<string, { value: string; label: string; count: number }[]> {
  const facets: Record<
    string,
    { value: string; label: string; count: number }[]
  > = {};

  // Only process multiSelect and select filters
  const facetableConfigs = filterConfig.filter(
    (c) => c.type === "multiSelect" || c.type === "select",
  );

  for (const config of facetableConfigs) {
    const fieldName = config.fieldName;
    const counts = new Map<string, number>();

    // Count occurrences
    for (const row of data) {
      const value = row[fieldName];
      if (value !== null && value !== undefined && value !== "") {
        const strValue = String(value);
        counts.set(strValue, (counts.get(strValue) || 0) + 1);
      }
    }

    // Convert to options array, sorted by count
    facets[fieldName] = Array.from(counts.entries())
      .map(([value, count]) => ({
        value,
        label: prettifyValue(value),
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }

  return facets;
}

// ============================================================================
// Range Bounds Utilities
// ============================================================================

/**
 * Calculates min/max bounds for range filters from data
 */
export function calculateRangeBounds(
  data: Record<string, unknown>[],
  fieldName: string,
): { min: number; max: number } | null {
  const values = data
    .map((row) => row[fieldName])
    .filter((v) => v !== null && v !== undefined)
    .map((v) => Number(v))
    .filter((v) => !isNaN(v));

  if (values.length === 0) return null;

  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

/**
 * Updates range configs with actual bounds from data
 */
export function enrichRangeConfigsFromData(
  filterConfig: FilterFieldConfig[],
  data: Record<string, unknown>[],
): FilterFieldConfig[] {
  return filterConfig.map((config) => {
    if (config.type !== "range") return config;

    const bounds = calculateRangeBounds(data, config.fieldName);
    if (!bounds) return config;

    return {
      ...config,
      rangeConfig: {
        ...config.rangeConfig,
        min: bounds.min,
        max: bounds.max,
      },
    };
  });
}

// ============================================================================
// Backend Filter Schema Conversion
// ============================================================================

/**
 * Checks if a label is a placeholder/default value that should be replaced
 * with a prettified version of the field name
 */
function isPlaceholderLabel(label: string | undefined): boolean {
  if (!label) return true;
  const normalizedLabel = label.toLowerCase().trim();
  const placeholders = new Set(["default", "null", "undefined", "none", ""]);
  return placeholders.has(normalizedLabel);
}

/**
 * Converts backend filter schema to frontend FilterFieldConfig format.
 *
 * This allows the frontend to get filter configuration from the backend
 * without loading any records, enabling immediate filter UI rendering.
 */
export function convertBackendSchemaToFilterConfig(
  schema: BackendFilterSchema,
): FilterFieldConfig[] {
  // Fields to exclude from filtering
  const excludeFields = new Set([
    "id",
    "source_uid",
    "dataset_fk",
    "Searchable Sections",
    "searchable_sections",
    "extracted metadata",
    "Extracted Metadata",
    "extracted_metadata",
  ]);

  return schema.fields
    .filter((field) => !excludeFields.has(field.name))
    .map((field, index) => {
      // Use field.label only if it's not a placeholder value like "default"
      const label = isPlaceholderLabel(field.label)
        ? prettifyFieldLabel(field.name)
        : field.label;

      // Determine the filter type - smart override for low-cardinality numeric fields
      const filterType = mapBackendFilterType(
        field.filter_type,
        field.type,
        field.distinct_count,
        typeof field.min_value === "number" ? field.min_value : undefined,
        typeof field.max_value === "number" ? field.max_value : undefined,
      );

      const baseConfig: FilterFieldConfig = {
        fieldName: field.name,
        label,
        type: filterType,
        defaultExpanded: index < 3,
        order: index,
        showCounts: true,
      };

      // Add type-specific configurations
      switch (baseConfig.type) {
        case "multiSelect":
        case "select":
          return {
            ...baseConfig,
            searchable: (field.distinct_count ?? 0) > 10,
            maxVisibleOptions: 8,
          };

        case "histogram":
          return {
            ...baseConfig,
            histogramOrientation: "horizontal",
            histogramShowPercentages: false,
          };

        case "dateRange":
          return {
            ...baseConfig,
            datePresets: DEFAULT_DATE_PRESETS,
          };

        case "text":
          return {
            ...baseConfig,
            placeholder: `Search ${label.toLowerCase()}...`,
          };

        default:
          return baseConfig;
      }
    });
}

/**
 * Threshold for treating numeric fields as multiSelect instead of range.
 * If a numeric field has <= this many distinct values, show checkboxes instead of a slider.
 */
const LOW_CARDINALITY_THRESHOLD = 20;

/**
 * For integer fields, if the range (max - min) is <= this value, treat as multiSelect.
 * This is a fallback when distinct_count is not available.
 * E.g., year field with min=2023, max=2024 → range of 1 → use multiSelect
 */
const LOW_RANGE_THRESHOLD = 20;

/**
 * Maps backend filter_type to frontend filter type
 *
 * Smart overrides:
 * - Numeric fields with low distinct_count → multiSelect (checkboxes) instead of range (slider)
 *   This handles cases like "year" with only 2-3 values where a slider is awkward
 * - Integer fields with small range (max - min) → multiSelect as fallback when distinct_count unavailable
 */
function mapBackendFilterType(
  backendFilterType: string,
  backendDataType: string,
  distinctCount?: number,
  minValue?: number,
  maxValue?: number,
): FilterFieldConfig["type"] {
  // Smart override #1: Use distinct_count if available
  if (
    (backendDataType === "int" || backendDataType === "float") &&
    distinctCount !== undefined &&
    distinctCount > 0 &&
    distinctCount <= LOW_CARDINALITY_THRESHOLD
  ) {
    return "multiSelect";
  }

  // Smart override #2: For integers, check if the range is small (fallback when distinct_count unavailable)
  // Only for integers since we can enumerate all values between min and max
  if (
    backendDataType === "int" &&
    minValue !== undefined &&
    maxValue !== undefined &&
    typeof minValue === "number" &&
    typeof maxValue === "number" &&
    maxValue - minValue >= 0 &&
    maxValue - minValue <= LOW_RANGE_THRESHOLD
  ) {
    return "multiSelect";
  }

  // Direct mapping from backend filter_type
  // Note: "range" maps to "histogram" for read-only distribution display
  const typeMap: Record<string, FilterFieldConfig["type"]> = {
    multiSelect: "multiSelect",
    select: "select",
    text: "text",
    range: "histogram", // Show histogram instead of interactive slider
    boolean: "boolean",
    dateRange: "dateRange",
    histogram: "histogram",
  };

  if (typeMap[backendFilterType]) {
    return typeMap[backendFilterType];
  }

  // Fallback based on data type
  switch (backendDataType) {
    case "bool":
      return "boolean";
    case "int":
    case "float":
      return "histogram"; // Show histogram instead of interactive slider
    case "datetime":
      return "dateRange";
    default:
      return "text";
  }
}

/**
 * Prettifies a field name for display as a label
 */
function prettifyFieldLabel(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Converts backend facets response to the format expected by filter components
 */
export function convertBackendFacetsToFilterOptions(
  facetsResponse: BackendFacetsResponse,
): Record<string, FilterOption[]> {
  const result: Record<string, FilterOption[]> = {};

  // Convert categorical facets
  for (const [fieldName, options] of Object.entries(
    facetsResponse.facets || {},
  )) {
    result[fieldName] = options.map((opt) => ({
      value: opt.value,
      label: opt.label || prettifyValue(opt.value),
      count: opt.count,
    }));
  }

  // Convert histogram buckets to FilterOption format for histogram display
  // The histogram component expects options with label and count
  for (const [fieldName, histogram] of Object.entries(
    facetsResponse.numeric_histograms || {},
  )) {
    result[fieldName] = histogram.buckets.map((bucket) => ({
      value: bucket.label, // Use label as value since histograms are read-only
      label: bucket.label,
      count: bucket.count,
    }));
  }

  return result;
}

/**
 * Generates synthetic facet options for integer fields based on min/max values.
 * Used as a fallback when the backend doesn't return facets for fields we've
 * converted to multiSelect (e.g., year with only 2 values).
 */
export function generateSyntheticFacetsForIntegerFields(
  schema: BackendFilterSchema,
  existingFacets: Record<string, FilterOption[]>,
): Record<string, FilterOption[]> {
  const result = { ...existingFacets };

  for (const field of schema.fields) {
    // Skip if we already have facets for this field
    if (result[field.name] && result[field.name].length > 0) {
      continue;
    }

    // Only generate for integer fields with small range that we've converted to multiSelect
    if (
      field.type === "int" &&
      typeof field.min_value === "number" &&
      typeof field.max_value === "number" &&
      field.max_value - field.min_value >= 0 &&
      field.max_value - field.min_value <= LOW_RANGE_THRESHOLD
    ) {
      const options: FilterOption[] = [];
      // Generate options for each integer value in the range
      for (let i = field.min_value; i <= field.max_value; i++) {
        options.push({
          value: String(i),
          label: String(i),
          // Count is unknown, so we don't show it
        });
      }
      result[field.name] = options;
    }
  }

  return result;
}

/**
 * Gets the list of fields that need facet data from the backend
 *
 * This includes:
 * - Fields explicitly marked as multiSelect/select by the backend
 * - Low-cardinality numeric fields that we convert to multiSelect on the frontend
 * - Integer fields with small range (max - min <= threshold)
 * - High-cardinality numeric fields that will be rendered as histograms
 */
export function getMultiSelectFieldsFromSchema(
  schema: BackendFilterSchema,
): string[] {
  return schema.fields
    .filter((field) => {
      // Explicitly multiSelect/select from backend
      if (
        field.filter_type === "multiSelect" ||
        field.filter_type === "select"
      ) {
        return true;
      }

      // Low-cardinality numeric fields that we convert to multiSelect (using distinct_count)
      if (
        (field.type === "int" || field.type === "float") &&
        field.distinct_count !== undefined &&
        field.distinct_count > 0 &&
        field.distinct_count <= LOW_CARDINALITY_THRESHOLD
      ) {
        return true;
      }

      // Integer fields with small range (fallback when distinct_count unavailable)
      if (
        field.type === "int" &&
        typeof field.min_value === "number" &&
        typeof field.max_value === "number" &&
        field.max_value - field.min_value >= 0 &&
        field.max_value - field.min_value <= LOW_RANGE_THRESHOLD
      ) {
        return true;
      }

      // High-cardinality numeric fields that will be rendered as histograms
      // These need facet data to show distribution buckets
      if (
        field.filter_type === "range" &&
        (field.type === "int" || field.type === "float")
      ) {
        return true;
      }

      return false;
    })
    .map((field) => field.name);
}

/**
 * Converts applied filters to a format suitable for the backend facets endpoint
 * This allows getting updated counts based on current filter selections
 */
export function appliedFiltersToFacetParams(
  appliedFilters: Array<{
    fieldName: string;
    operator: string;
    value: string | string[];
  }>,
): Record<string, string> {
  const params: Record<string, string> = {};

  for (const filter of appliedFilters) {
    const value = Array.isArray(filter.value)
      ? filter.value.join(",")
      : filter.value;

    switch (filter.operator) {
      case "eq":
        params[filter.fieldName] = value;
        break;
      case "like":
        params[filter.fieldName] = `~${value}`;
        break;
      case "ge":
        params[`${filter.fieldName}_min`] = value;
        break;
      case "le":
        params[`${filter.fieldName}_max`] = value;
        break;
      default:
        params[filter.fieldName] = value;
    }
  }

  return params;
}
